import { useEffect, useRef, useState } from "react";
import { reduced } from "../components/motion/Reveal";

/* Shared mechanics for the price charts (home floor + investor page).
   A time series is plotted the way it was recorded – left to right. The fill follows the pen, a marker rides the pen
   and lands on the last close. Changing period morphs the painted curve into the new one instead of redrawing.
   Everything is written straight to the SVG nodes from one rAF loop: no React render per frame. */

export const DRAW_MS = 1600, DRAW_DELAY = 220, MORPH_MS = 650, MORPH_N = 400;
export const easeOutCubic = (p) => 1 - Math.pow(1 - p, 3);
export const easeInOut = (p) => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2);

export const toPath = (ys, xw) => {
  const n = ys.length; let d = "";
  for (let i = 0; i < n; i++) d += `${i ? "L" : "M"}${((i / Math.max(1, n - 1)) * xw).toFixed(1)},${ys[i].toFixed(1)}`;
  return d;
};

/* Linear resample to a fixed point count so two series of different length can be interpolated.
   Only used for in-flight morph frames – the resting curve is always painted from the exact closes. */
export const resample = (vals, n) => {
  const m = vals.length, out = new Array(n);
  if (m === 1) return out.fill(vals[0]);
  for (let i = 0; i < n; i++) {
    const t = (i / (n - 1)) * (m - 1), k = Math.floor(t), f = t - k;
    out[i] = k + 1 < m ? vals[k] * (1 - f) + vals[k + 1] * f : vals[k];
  }
  return out;
};

const yAt = (ys, frac) => { const fi = frac * (ys.length - 1), k = Math.floor(fi), f = fi - k; return k + 1 < ys.length ? ys[k] * (1 - f) + ys[k + 1] * f : ys[k]; };

/**
 * @param ys       y values (chart units) for the current series, or null while loading
 * @param xw       x extent of the plot in chart units
 * @param yBase    y of the baseline the area closes to
 * @param seriesKey identifies the series (e.g. range). Same key + new ys = resize → instant repaint; new key = morph.
 * @param ready    start the first plot when true (visibility, data loaded, parent choreography…)
 * @param placeDot (x, y) → position the marker; coordinates in chart units
 * @returns { lineRef, areaRef, wipeRef, phase }  phase: "idle" | "drawing" | "drawn"
 */
export const usePlot = ({ ys, xw, yBase, seriesKey, ready, placeDot }) => {
  const lineRef = useRef(null), areaRef = useRef(null), wipeRef = useRef(null);
  const [phase, setPhase] = useState("idle");
  const phaseRef = useRef("idle");
  const painted = useRef(null);  // ys currently on screen (exact or in-flight)
  const keyRef = useRef(null);
  const rafRef = useRef(0);
  const dotRef = useRef(placeDot);
  dotRef.current = placeDot;

  useEffect(() => {
    if (!ys?.length) return;
    const setP = (v) => { phaseRef.current = v; setPhase(v); };
    const paint = (arr) => {
      const d = toPath(arr, xw);
      lineRef.current?.setAttribute("d", d);
      areaRef.current?.setAttribute("d", `${d} L${xw},${yBase} L0,${yBase} Z`);
      painted.current = arr;
    };
    const wipe = (frac) => wipeRef.current?.setAttribute("width", String(Math.max(0, xw * frac)));
    const dot = (frac, y) => dotRef.current?.(frac * xw, y);

    cancelAnimationFrame(rafRef.current);

    if (phaseRef.current === "idle") {
      if (!ready) return;
      paint(ys); keyRef.current = seriesKey;
      if (reduced()) { wipe(1); dot(1, yAt(ys, 1)); setP("drawn"); return; }
      setP("drawing");
      let t0;
      const tick = (t) => {
        if (t0 == null) t0 = t;
        const p = Math.min(1, Math.max(0, (t - t0 - DRAW_DELAY) / DRAW_MS));
        const e = easeOutCubic(p);
        wipe(e); dot(e, yAt(painted.current, e));
        if (p < 1) rafRef.current = requestAnimationFrame(tick); else setP("drawn");
      };
      rafRef.current = requestAnimationFrame(tick);
      return;
    }

    const sameSeries = keyRef.current === seriesKey;
    keyRef.current = seriesKey;
    if (sameSeries || phaseRef.current === "drawing" || reduced()) {
      // resize / data refresh / still plotting: repaint in place, keep the wipe where it is
      paint(ys);
      if (phaseRef.current === "drawn") { wipe(1); dot(1, yAt(ys, 1)); }
      return;
    }

    // Morph the painted curve into the new one, then settle on the exact values.
    const a = resample(painted.current, MORPH_N), b = resample(ys, MORPH_N);
    const buf = new Array(MORPH_N);
    let t0;
    const tick = (t) => {
      if (t0 == null) t0 = t;
      const p = Math.min(1, (t - t0) / MORPH_MS);
      if (p < 1) {
        const e = easeInOut(p);
        for (let i = 0; i < MORPH_N; i++) buf[i] = a[i] + (b[i] - a[i]) * e;
        paint(buf); wipe(1); dot(1, buf[MORPH_N - 1]);
        rafRef.current = requestAnimationFrame(tick);
      } else { paint(ys); wipe(1); dot(1, yAt(ys, 1)); }
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [ys, xw, yBase, seriesKey, ready]);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  return { lineRef, areaRef, wipeRef, phase };
};
